// src/app/pages/home/Home.js
import React, { Suspense, startTransition, useRef, useEffect, useState } from "react";
import HeroCarousel from "../../components/carouselSlider/HeroCarousel";
import SearchForm from "../../components/searshForms/searchForm/SearchForm";
import GridCard from "../../components/trips/gridCard/GridCard";
import MobileStore from "../../components/mobileStore/MobileStore";

import { Button, SearchSummary, SectionHeader } from "@zorni/components-lib";

import { useNavigate } from "react-router-dom";
import useReactSimpleMatchMedia from "react-simple-matchmedia";
import { useTranslation } from "react-i18next";
import { gridCardItems } from "./HomeAPI";
import styles from "./Home.module.css";
import useIntersection from "../../hooks/useIntersection";
import { useHeaderDock } from "../../providers/HeaderDockContext";
import { useSearch } from "../../hooks/SearchContext";
import { useRecentVisitedTrips } from "../../components/tripDetails/detailsTrip/hooks/useVisitedTrips";
import ThumbnailCard from "../../components/trips/thumbnailCard/ThumbnailCard";
import FlashOfferSection from "../../components/flashOffer/FlashOfferSection";
import NewsletterSection from "../../components/newsletter/NewsletterSection";

export default function Home() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  // Téléphone uniquement : les tablettes (iPad mini inclus, 744px+) gardent le
  // hero + le SearchForm complet plutôt que le pavé de recherche mobile.
  const isMobile = useReactSimpleMatchMedia("(max-width: 599.98px)");
  const [homeTripsTotal, setHomeTripsTotal] = useState(0);
  const { trips: recentTrips, clear: clearRecentTrips } = useRecentVisitedTrips();

  // 🔗 state de recherche global (même que le Header)
  const { search, updateSearch, setSheetOpen } = useSearch();

  const slides = [
    {
      id: "1",
      city: "Fès",
      country: "Maroc",
      img: "https://cdn.getyourguide.com/img/tour/5824f8b50d3f3.jpeg/145.jpg",
      subtitle: "Médina, artisanat & riads",
    },
    {
      id: "2",
      city: "Marrakech",
      country: "Maroc",
      img: "https://res.cloudinary.com/hello-tickets/image/upload/c_limit,f_auto,q_auto,w_1300/v1645588616/j4ssef2gssrjvzxbbqpr.jpg",
      subtitle: "Jemaa el-Fna & jardins",
    },
    {
      id: "3",
      city: "Casablanca",
      country: "Maroc",
      img: "https://www.visitmorocco.com/sites/default/files/styles/thumbnail_events_slider/public/thumbnails/image/city-panorama.-casablanca-morocco.-africa-marianna-ianovska.jpg?itok=h4FjZSIp",
      subtitle: "Corniche & Hassan II",
    },
    {
      id: "4",
      city: "Rabat",
      country: "Maroc",
      img: "https://www.tracedirecte.com/media/original_images/rabat-maroc.jpg.1920x0_q85_format-jpg.jpg",
      subtitle: "Tour Hassan & Oudayas",
    },
    {
      id: "5",
      city: "Tanger",
      country: "Maroc",
      img: "https://images.unsplash.com/photo-1533501747004-381b96042e88?auto=format&fit=crop&q=80&w=1600",
      subtitle: "Détente entre mer & médina",
    },
  ];

  // =========================
  // Dock du header (Home only)
  // =========================
  const searchDockRef = useRef(null);
  const isSearchDockVisible = useIntersection(searchDockRef, { threshold: 0.2 });
  const { setDocked } = useHeaderDock();

  /* Le formulaire doit chevaucher le hero à parts égales : moitié sur
     l'image, moitié sur le blanc. C'est une marge négative de la moitié de sa
     hauteur qui donne ça — et elle resserre le flux, contrairement à un
     transform qui laisserait un vide dessous. On mesure plutôt que de coder
     47px en dur : la carte peut changer de hauteur selon la largeur. */
  useEffect(() => {
    const el = searchDockRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;

    const apply = () => {
      const h = el.getBoundingClientRect().height;
      if (h > 0) el.style.setProperty("--dock-h", `${Math.round(h)}px`);
    };

    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    // On docke le header seulement quand le formulaire hero n'est plus visible.
    setDocked(!isSearchDockVisible);

    // ✅ Quand on quitte Home, on reset pour éviter que ça reste sur les autres pages
    return () => {
      setDocked(false);
    };
  }, [isSearchDockVisible, setDocked]);

  return (
    <>
      {!isMobile && (
        <>
          <HeroCarousel
            slides={slides}
            autoPlayDelay={6500}
            onExplore={(slide) =>
              startTransition(() => {
                navigate(`/trips?destination=${encodeURIComponent(slide.city)}`);
              })
            }
          />

          {/* Search docké SOUS le hero (chevauche visuellement) */}
          <div ref={searchDockRef} className={styles.searchDock}>
            <SearchForm value={search} onChange={(patch) => updateSearch(patch)} />
          </div>
        </>
      )}

      {isMobile && (
        /* Récapitulatif de recherche : ouvre la feuille de recherche mobile.
           Même référence que le formulaire desktop : quand il sort de l'écran,
           sa version compacte prend le relais dans l'en-tête. */
        <div ref={searchDockRef} className={styles.mobileSearchWrapper}>
          <SearchSummary
            value={search}
            onClick={() => setSheetOpen(true)}
            editLabel={t("components.searchSummary.search", "Rechercher")}
            t={t}
          />
        </div>
      )}

      {/* Offre flash — entre la recherche et les voyages, comme « 01 - home v5 ».
          Ne rend rien s'il n'y a pas d'offre en cours. */}
      <FlashOfferSection />

      {/* Section cartes */}
      <section className={`${styles.zorni_about_area} ${styles.section_padding_search_form}`}>
        
        <ThumbnailCard items={recentTrips} onClear={clearRecentTrips} />
        
        {/* Intro (titre + sous-titre) */}
        <SectionHeader
          className={styles.tripsIntro}
          kicker={t("portal.home.tripsKicker", "Sélectionné pour vous")}
          title={t("portal.home.tripsTitle")}
          subtitle={t("portal.home.tripsSubtitle")}
        />

        <Suspense fallback={<div>Loading…</div>}>
          <GridCard
            items={gridCardItems}
            onTotalElementsChange={setHomeTripsTotal}
            destination={search.destination || undefined}
            startDate={search.startDate || undefined}
            endDate={search.endDate || undefined}
            adult={search.adults || undefined}
            child={search.children || undefined}
            baby={search.babies || undefined}
          />
        </Suspense>

        {homeTripsTotal > 15 && (
          <div className={styles.show_more_trips_bt}>
            <Button
              label={t("portal.actions.showMore")}
              onClick={() =>
                startTransition(() => {
                  navigate("/trips");
                })
              }
              size="medium"
              variant="primary"
            />
          </div>
        )}
      </section>

      {/* Application mobile — après les voyages, comme « 01 - home v5 ». */}
      <MobileStore />

      {/* Newsletter — dernière section avant le pied de page. */}
      <NewsletterSection />
    </>
  );
}
